/**
 * RiderPricesPanel · v2.6.9 batch BP (CMS-driven)
 * ================================================
 * Pulls the live config from /api/rider-prices/config (public endpoint).
 * Admins manage all copy + rates from `Admin Console → Administration →
 * Rider Price Page`. No code changes required to update rates anymore.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Plane, Check, Shield, CreditCard, Banknote, Smartphone, ArrowRight, Info, AlertTriangle, Loader2, Zap, Calculator } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import customerApi from "@/lib/customerApi";
import api from "@/lib/api";
import { fmtUsd } from "@/lib/format";
import { formatApiError } from "@/lib/errors";
import { toast } from "sonner";

const PAYMENT_ICONS = {
  cash: { Icon: Banknote, accent: "text-emerald-300" },
  "debit / credit card": { Icon: CreditCard, accent: "text-indigo-300" },
  card: { Icon: CreditCard, accent: "text-indigo-300" },
  "cash app": { Icon: Smartphone, accent: "text-emerald-300" },
  venmo: { Icon: Smartphone, accent: "text-sky-300" },
};
function paymentMeta(label) {
  const key = (label || "").trim().toLowerCase();
  return PAYMENT_ICONS[key] || { Icon: CreditCard, accent: "text-zinc-300" };
}

const ACCENT = {
  emerald: {
    ring: "border-emerald-500/60",
    glow: "shadow-emerald-500/20",
    chip: "text-emerald-300",
    gradient: "from-emerald-950/40 to-zinc-950",
    btn: "bg-emerald-400 hover:bg-emerald-300 text-zinc-950",
    Icon: MapPin,
  },
  amber: {
    ring: "border-amber-400/70",
    glow: "shadow-amber-400/30",
    chip: "text-amber-300",
    gradient: "from-amber-950/40 to-zinc-950",
    btn: "bg-amber-400 hover:bg-amber-300 text-zinc-950",
    Icon: Plane,
  },
  indigo: {
    ring: "border-indigo-500/60",
    glow: "shadow-indigo-500/20",
    chip: "text-indigo-300",
    gradient: "from-indigo-950/40 to-zinc-950",
    btn: "bg-indigo-400 hover:bg-indigo-300 text-zinc-950",
    Icon: MapPin,
  },
  rose: {
    ring: "border-rose-500/60",
    glow: "shadow-rose-500/20",
    chip: "text-rose-300",
    gradient: "from-rose-950/40 to-zinc-950",
    btn: "bg-rose-400 hover:bg-rose-300 text-zinc-950",
    Icon: MapPin,
  },
};

export default function RiderPricesPanel({ onBookRide }) {
  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);

  // v2.6.9 batch BP · pull the live CMS config (public endpoint).
  useEffect(() => {
    let cancelled = false;
    api.get("/rider-prices/config")
      .then((r) => { if (!cancelled) setCfg(r.data); })
      .catch(() => { if (!cancelled) setCfg(null); })
      .finally(() => { if (!cancelled) setCfgLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Instant Quote widget (unchanged from batch BO).
  const [iqOrigin, setIqOrigin] = useState("");
  const [iqDest, setIqDest] = useState("");
  const [iqLoading, setIqLoading] = useState(false);
  const [iqQuote, setIqQuote] = useState(null);
  const getInstantQuote = async () => {
    if (!iqOrigin || !iqDest) return toast.error("Enter both pickup and drop-off");
    setIqLoading(true); setIqQuote(null);
    try {
      const { data } = await customerApi.post("/customer/quote", {
        ride_type: "single", origin: iqOrigin, destination: iqDest, passengers: 1, stops: [],
      });
      setIqQuote({ total: data.subtotal, miles: data.miles, minutes: data.minutes, breakdown: data.breakdown });
    } catch (e) {
      toast.error(formatApiError(e, "Could not fetch quote"), { duration: 8000 });
    } finally { setIqLoading(false); }
  };

  if (cfgLoading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500" data-testid="rider-prices-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto"/>
        <div className="mt-2 text-xs uppercase tracking-widest">Loading prices…</div>
      </Card>
    );
  }
  if (!cfg) {
    return (
      <Card className="bg-red-500/10 border-red-500/40 text-red-200 p-5 text-sm" data-testid="rider-prices-error">
        Could not load pricing right now. Please refresh the page.
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="rider-prices-panel">
      {/* ── Instant Quote widget ─────────────────────────────────────────── */}
      <Card className="bg-gradient-to-br from-amber-400/15 via-zinc-950 to-zinc-950 border-2 border-amber-400/60 p-5 shadow-2xl shadow-amber-500/20" data-testid="rider-prices-instant-quote">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-9 w-9 rounded-xl bg-amber-400 text-zinc-950 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/30">
            <Zap className="h-5 w-5"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold">Instant Quote</div>
            <h3 className="text-lg font-extrabold text-zinc-100 leading-tight">Get a live price in seconds</h3>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Pickup</div>
            <AddressAutocomplete value={iqOrigin} onChange={setIqOrigin} placeholder="Pickup address" testid="prices-iq-origin"/>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Drop-off</div>
            <AddressAutocomplete value={iqDest} onChange={setIqDest} placeholder="Drop-off address" testid="prices-iq-destination"/>
          </div>
        </div>
        <Button
          data-testid="prices-iq-btn"
          onClick={getInstantQuote}
          disabled={iqLoading}
          className="w-full mt-3 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold tracking-wider uppercase"
        >
          {iqLoading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Calculating…</> : <><Calculator className="h-4 w-4 mr-1.5"/>Get Instant Quote</>}
        </Button>
        {iqQuote && (
          <div className="mt-4 rounded-xl border-2 border-amber-400/40 p-4 bg-[#0A0A0A]" data-testid="prices-iq-result">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Estimated Fare</div>
                <div className="meter-display text-4xl font-extrabold leading-none mt-1" style={{ color: "#FFD400" }} data-testid="prices-iq-total">
                  {fmtUsd(iqQuote.total)}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">{(iqQuote.miles || 0).toFixed(1)} mi · {Math.round(iqQuote.minutes || 0)} min</div>
              </div>
              <Button
                data-testid="prices-iq-book-btn"
                onClick={() => onBookRide && onBookRide("book")}
                className="bg-emerald-400 hover:bg-emerald-300 text-zinc-950 font-extrabold tracking-wider uppercase"
              >
                Book Now <ArrowRight className="h-4 w-4 ml-1.5"/>
              </Button>
            </div>
            <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-3">Live estimate · Subject to traffic + tolls + actual route</div>
          </div>
        )}
      </Card>

      {/* ── Commitment / Compliance block ───────────────────────────────── */}
      <Card className="bg-gradient-to-br from-amber-500/10 via-zinc-950 to-zinc-950 border-amber-400/30 p-5 shadow-lg shadow-amber-900/10" data-testid="rider-prices-commitment">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-400/15 border border-amber-400/40 flex items-center justify-center text-amber-300 flex-shrink-0">
            <Shield className="h-5 w-5"/>
          </div>
          <div className="flex-1 min-w-0 space-y-2.5 text-sm text-zinc-300 leading-relaxed whitespace-pre-line">
            <p>{cfg.commitment_text}</p>
            <p>{cfg.rate_change_text}</p>
          </div>
        </div>
      </Card>

      {/* ── Section header ─────────────────────────────────────────────── */}
      <div className="text-center px-2" data-testid="rider-prices-section-header">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold">{cfg.section_header}</div>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-zinc-100 mt-1">{cfg.section_subheader}</h2>
        <p className="text-xs text-zinc-500 mt-1.5 max-w-xl mx-auto">{cfg.section_caption}</p>
      </div>

      {/* ── Pricing panels (dynamic count) ────────────────────────────── */}
      <div className={`grid grid-cols-1 ${cfg.panels.length >= 2 ? "md:grid-cols-2" : ""} gap-4`} data-testid="rider-prices-panels">
        {cfg.panels.map((p) => {
          const a = ACCENT[p.color] || ACCENT.emerald;
          const Icon = a.Icon;
          return (
            <Card
              key={p.id}
              data-testid={`rider-prices-card-${p.id}`}
              className={`bg-gradient-to-b ${a.gradient} border-2 ${a.ring} p-6 shadow-xl ${a.glow} flex flex-col`}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className={`h-12 w-12 rounded-xl border-2 ${a.ring} flex items-center justify-center ${a.chip} flex-shrink-0`} style={{ backgroundColor: "#0A0A0A" }}>
                  <Icon className="h-6 w-6"/>
                </div>
                <div className="min-w-0">
                  <div className={`text-[10px] uppercase tracking-widest ${a.chip} font-bold`}>{p.label}</div>
                  <h3 className="text-xl font-extrabold text-zinc-100 leading-tight">{p.title}</h3>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{p.subtitle}</p>
                </div>
              </div>
              <ul className="space-y-1.5 mb-4" data-testid={`rider-prices-rates-${p.id}`}>
                {p.rates.map((r, i) => (
                  <li
                    key={`${r.k}-${i}`}
                    className={`flex items-baseline justify-between gap-3 px-3 py-2 rounded-md border ${r.hi ? `${a.ring} bg-zinc-950/60` : "border-zinc-800/80 bg-zinc-950/40"}`}
                  >
                    <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">{r.k}</span>
                    <span className={`meter-display text-sm font-extrabold ${r.hi ? a.chip : "text-zinc-100"}`}>{r.v}</span>
                  </li>
                ))}
              </ul>
              <div className={`rounded-lg border-2 ${a.ring} px-4 py-3 mb-4`} style={{ backgroundColor: "#0A0A0A" }} data-testid={`rider-prices-estimate-${p.id}`}>
                <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{p.estimate_label}</div>
                <div className={`meter-display text-2xl font-extrabold ${a.chip} leading-none mt-1`}>{p.estimate}</div>
              </div>
              <Button
                data-testid={`rider-prices-book-${p.id}`}
                onClick={() => onBookRide && onBookRide("book")}
                className={`w-full ${a.btn} font-extrabold tracking-wider uppercase`}
              >
                Book Ride <ArrowRight className="h-4 w-4 ml-1.5"/>
              </Button>
              <div className="text-[9px] text-center text-zinc-600 uppercase tracking-widest mt-2">Quote auto-generated · No hidden fees</div>
            </Card>
          );
        })}
      </div>

      {/* ── Long-Distance Travel Notice ────────────────────────────────── */}
      <Card className="bg-zinc-900 border-amber-400/30 p-5" data-testid="rider-prices-long-distance">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-amber-300 flex-shrink-0">
            <Plane className="h-5 w-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-extrabold text-zinc-100">{cfg.long_distance_title}</h3>
            <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed whitespace-pre-line">{cfg.long_distance_text}</p>
          </div>
        </div>
      </Card>

      {/* ── Payment Information ────────────────────────────────────────── */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="rider-prices-payment">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="h-4 w-4 text-amber-400"/>
          <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-100">Payment Information</h3>
        </div>
        <p className="text-sm text-zinc-400 leading-relaxed mb-3 whitespace-pre-line">{cfg.payment_intro_text}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="rider-prices-payment-methods">
          {cfg.payment_methods.map((label) => {
            const { Icon: I, accent } = paymentMeta(label);
            return (
              <div key={label} className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
                <I className={`h-4 w-4 ${accent} flex-shrink-0`}/>
                <span className="text-xs font-semibold text-zinc-200 truncate">{label}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-400/30 rounded-md p-3" data-testid="rider-prices-cc-fee">
          <Info className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0"/>
          <div className="text-xs text-zinc-300 leading-relaxed whitespace-pre-line">
            <span className="font-semibold text-amber-300">Payment Processing:</span> {cfg.card_processing_fee_text}
          </div>
        </div>
      </Card>

      {/* ── Footer note ────────────────────────────────────────────────── */}
      <div className="flex items-start gap-2 text-[11px] text-zinc-500 px-2" data-testid="rider-prices-footer">
        <AlertTriangle className="h-3.5 w-3.5 text-zinc-600 mt-0.5 flex-shrink-0"/>
        <span className="whitespace-pre-line">{cfg.footer_disclaimer}</span>
      </div>
    </div>
  );
}
